import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { OnScoreTipCoordinates } from '../utils/onScoreTipCoordinates';
import '../styles/tips.css';

const ScorePositionSelector = ({ 
  isOpen, 
  onClose, 
  onPositionSelected,
  osmdInstance,
  visibleParts = [],
  musicxmlId,
  editingTip = null
}) => {
  const { t } = useLanguage();
  const [coordinateSystem, setCoordinateSystem] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [lastMouseEvent, setLastMouseEvent] = useState(null);
  const [testDots, setTestDots] = useState([]); // For debugging coordinate accuracy
  const overlayRef = useRef(null);
  const scoreContainerRef = useRef(null);

  // Initialize coordinate system when modal opens
  useEffect(() => {
    if (isOpen && osmdInstance) {
      const coords = new OnScoreTipCoordinates(osmdInstance);
      setCoordinateSystem(coords);
      
      return () => {
        coords.dispose();
      };
    }
  }, [isOpen, osmdInstance]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedPosition(null);
      setTestDots([]); // Clear test dots when modal opens
    }
  }, [isOpen]);

  const handleScoreClick = useCallback((event) => {
    const eventInfo = {
      type: 'click',
      time: new Date().toLocaleTimeString(),
      x: event.clientX,
      y: event.clientY,
      target: event.target.tagName,
      targetClass: event.target.className
    };
    
    setLastMouseEvent(eventInfo);
    
    if (!coordinateSystem) {
      return;
    }
    
    // Only prevent default for actual position selection
    event.preventDefault();
    
    // Use coordinates relative to the score SVG element to ensure consistency
    const scoreContainer = scoreContainerRef.current;
    const svgElement = scoreContainer?.querySelector('svg');
    
    let scoreRelativeX, scoreRelativeY;
    if (svgElement) {
      const svgRect = svgElement.getBoundingClientRect();
      scoreRelativeX = event.clientX - svgRect.left;
      scoreRelativeY = event.clientY - svgRect.top;
    } else {
      scoreRelativeX = event.nativeEvent.offsetX;
      scoreRelativeY = event.nativeEvent.offsetY;
    }
    
    const targetRect = event.target.getBoundingClientRect();
    if (svgElement) {
      const svgRect = svgElement.getBoundingClientRect();
      const containerRect = scoreContainer.getBoundingClientRect();
      // console.log(`TIP CREATION: Click at score-relative(${scoreRelativeX}, ${scoreRelativeY}). Modal SVG offset in container: (${svgRect.left - containerRect.left}, ${svgRect.top - containerRect.top})`);
    } else {
      // console.log(`TIP CREATION: Click at score-relative(${scoreRelativeX}, ${scoreRelativeY}) - no SVG found`);
    }
    
    // Debug test dots removed for production
    
    // Convert to musical anchor using client coordinates
    const musicalAnchor = coordinateSystem.screenToMusicalAnchor(event.clientX, event.clientY, visibleParts);
    
    if (musicalAnchor) {
      // Store the score-relative position and SVG offset info for proper conversion
      musicalAnchor.pinX = scoreRelativeX;
      musicalAnchor.pinY = scoreRelativeY;
      
      // Store modal SVG offset for later conversion
      if (svgElement) {
        const svgRect = svgElement.getBoundingClientRect();
        const containerRect = scoreContainer.getBoundingClientRect();
        musicalAnchor.modalSvgOffsetX = svgRect.left - containerRect.left;
        musicalAnchor.modalSvgOffsetY = svgRect.top - containerRect.top;
      }
      setSelectedPosition(musicalAnchor);
    } else {
      // For testing purposes, create a fallback anchor
      
      const fallbackAnchor = {
        measureNumber: 1,
        beat: 0.5,
        staffIndex: 0,
        relativeX: 0.5,
        relativeY: 0.5,
        visibleParts: [...visibleParts],
        timestamp: Date.now(),
        pinX: scoreRelativeX,
        pinY: scoreRelativeY
      };
      setSelectedPosition(fallbackAnchor);
    }
  }, [coordinateSystem, visibleParts]);

  const handleConfirmPosition = () => {
    if (selectedPosition) {
      onPositionSelected(selectedPosition);
      onClose();
    }
  };


  const getPositionDescription = (position) => {
    if (!position) return '';
    
    let desc = `${t('tips.measure')} ${position.measureNumber}`;
    
    if (position.beat !== undefined) {
      desc += `, ${t('tips.beat')} ${(position.beat * 4 + 1).toFixed(1)}`;
    }
    
    if (position.staffIndex !== undefined) {
      desc += `, Staff ${position.staffIndex + 1}`;
    }
    
    return desc;
  };

  const renderScoreClone = () => {
    if (!osmdInstance?.container) {
      return (
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#6b7280' 
        }}>
          Score not available
        </div>
      );
    }

    // Clone the score container
    const originalContainer = osmdInstance.container;
    const clonedContainer = originalContainer.cloneNode(true);
    
    // Remove any existing IDs to avoid conflicts
    const elementsWithIds = clonedContainer.querySelectorAll('[id]');
    elementsWithIds.forEach(el => {
      el.removeAttribute('id');
    });

    // Disable all interactive behaviors on the cloned score
    const allElements = clonedContainer.querySelectorAll('*');
    allElements.forEach(el => {
      // Remove any event listeners and interactive attributes
      el.style.pointerEvents = 'none';
      el.removeAttribute('onclick');
      el.removeAttribute('onmousedown');
      el.removeAttribute('onmouseup');
      el.removeAttribute('onmouseover');
      el.removeAttribute('onmouseout');
      
      // Disable text selection
      el.style.userSelect = 'none';
      el.style.webkitUserSelect = 'none';
      el.style.msUserSelect = 'none';
      el.style.mozUserSelect = 'none';
    });

    // Dim existing tip pins in the cloned score (except the one being edited)
    const existingTipPins = clonedContainer.querySelectorAll('.tip-overlay-element.tip-pin');
    existingTipPins.forEach(pin => {
      // Check if this pin corresponds to the tip being edited
      let shouldDim = true;
      
      if (editingTip) {
        // Try to match by tooltip content (modern tooltips are child elements)
        const tooltip = pin.querySelector('.modern-tip-tooltip');
        if (tooltip && tooltip.textContent === editingTip.content) {
          shouldDim = false;
          // Mark this as the original pin with special styling
          pin.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.5), 0 4px 12px rgba(0, 0, 0, 0.4)';
          pin.style.animation = 'gentle-pulse 2s ease-in-out infinite';
          
          // Add the pulse animation CSS if it doesn't exist
          if (!document.getElementById('gentle-pulse-style')) {
            const style = document.createElement('style');
            style.id = 'gentle-pulse-style';
            style.textContent = `
              @keyframes gentle-pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.05); }
              }
            `;
            document.head.appendChild(style);
          }
        }
        // Fallback: match by title attribute if tooltip not found
        else if (pin.title === editingTip.content) {
          shouldDim = false;
          // Apply same special styling for fallback case
          pin.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.5), 0 4px 12px rgba(0, 0, 0, 0.4)';
          pin.style.animation = 'gentle-pulse 2s ease-in-out infinite';
        }
      }
      
      if (shouldDim) {
        pin.style.opacity = '0.3';
        pin.style.filter = 'grayscale(0.7) blur(0.5px)';
        pin.style.pointerEvents = 'none';
      }
    });

    // Also disable selection on the container itself
    clonedContainer.style.userSelect = 'none';
    clonedContainer.style.webkitUserSelect = 'none';
    clonedContainer.style.msUserSelect = 'none';
    clonedContainer.style.mozUserSelect = 'none';

    return (
      <div 
        ref={scoreContainerRef}
        style={{
          position: 'relative',
          minHeight: '400px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          padding: '20px',
          margin: '10px',
          // Disable text selection on the container
          userSelect: 'none',
          WebkitUserSelect: 'none',
          msUserSelect: 'none',
          MozUserSelect: 'none',
          // Make sure it's positioned for our coordinate calculations
          zIndex: 2
        }}
        dangerouslySetInnerHTML={{ __html: clonedContainer.innerHTML }}
        onSelectStart={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
      />
    );
  };

  if (!isOpen) return null;

  return (
    <div className="tip-modal-overlay" onClick={onClose}>
      <div 
        className="score-position-selector-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '1200px',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#065f46',
          color: '#f9fafb',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #374151',
          flexShrink: 0
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
            📍 {t('tips.selectPositionOnScore')}
          </h2>
          <button 
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#d1d5db',
              fontSize: '24px',
              cursor: 'pointer',
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Instructions */}
        <div style={{
          background: '#374151',
          padding: '12px 24px',
          borderBottom: '1px solid #4b5563',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {selectedPosition ? (
                <div style={{ color: '#10b981', fontSize: '14px' }}>
                  ✅ {t('tips.positionSelected', { pinX: selectedPosition.pinX, pinY: selectedPosition.pinY })}
                </div>
              ) : (
                <div style={{ color: '#e5e7eb', fontSize: '14px' }}>
                  🎯 {t('tips.clickAnywhereToSelect')}
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Score Display Area */}
        <div 
          ref={overlayRef}
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'auto', // Allow scrolling
            background: '#f8fafc',
            cursor: 'crosshair',
            // Prevent text selection 
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
          onClick={handleScoreClick}
          onSelectStart={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {/* Visual border - TEMPORARILY HIDDEN */}
          <div style={{ display: 'none' }}></div>

          {/* Debug test dots removed for production */}

          {/* Selection indicator - Convert SVG-relative coordinates to container-relative */}
          {selectedPosition && (() => {
            const scoreContainer = scoreContainerRef.current;
            const svgElement = scoreContainer?.querySelector('svg');
            if (!scoreContainer || !svgElement) return null;
            
            const svgRect = svgElement.getBoundingClientRect();
            const containerRect = scoreContainer.getBoundingClientRect();
            const svgOffsetX = svgRect.left - containerRect.left;
            const svgOffsetY = svgRect.top - containerRect.top;
            
            const indicatorX = (selectedPosition.pinX || 100) + svgOffsetX;
            const indicatorY = (selectedPosition.pinY || 100) + svgOffsetY;
            
            return (
              <div style={{
                position: 'absolute',
                left: indicatorX,
                top: indicatorY,
              width: '32px',
              height: '32px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '50%',
              border: '3px solid #ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              zIndex: 9999,
              pointerEvents: 'none'
              }}>
                📍
              </div>
            );
          })()}

          {/* Score Clone */}
          {renderScoreClone()}
        </div>

        {/* Action Buttons */}
        <div style={{
          padding: '16px 24px',
          background: '#111827',
          borderTop: '1px solid #374151',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                background: '#374151',
                color: '#d1d5db',
                border: '1px solid #4b5563',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {t('tips.cancel')}
            </button>
            <button
              onClick={handleConfirmPosition}
              disabled={!selectedPosition}
              style={{
                background: selectedPosition ? '#3b82f6' : '#6b7280',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: selectedPosition ? 'pointer' : 'not-allowed',
                opacity: selectedPosition ? 1 : 0.6
              }}
            >
              {t('tips.useThisPosition')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScorePositionSelector;